import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useSearchVendors } from '@/api/endpoints/vendor/useSearchVendors';
import { useUpdateSelectedVendors } from '@/api/endpoints/vendor/useUpdateSelectedVendors';
import { VendorSearchResult } from '@/api/models/vendor';
import { PublicRoutes, PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';

import VendorSearchResults from '@/components/vendor/VendorSearchResults';

interface LocationState {
  vendors: VendorSearchResult[];
  searchQuery: string;
  address: string;
  disclaimer?: string;
  search_query?: string; // Backend's modified search query
  searchId?: string; // Search tracking ID from backend
}

const VendorSearchResultsPage: React.FC = () => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  
  const updateSelectedVendorsMutation = useUpdateSelectedVendors();
  const searchVendorsMutation = useSearchVendors();
  const { user } = useAuth();

  const { 
    vendors = [], 
    searchQuery = '', 
    address = '', 
    disclaimer,
    search_query,
    searchId 
  } = location.state || {};

  useEffect(() => {
    // If user is already logged in, redirect to property manager portal
    if (user && location.state) {
      sessionStorage.setItem('vendorSearchState', JSON.stringify(location.state));
      history.replace(PMPRoutes.vendorSearchResults);
      return;
    }
    
    // If no vendors data, redirect back to home
    if (!location.state || !vendors || vendors.length === 0) {
      const timer = setTimeout(() => {
        history.push(PublicRoutes.home);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [location.state, vendors, history, user]);



  const handleNewSearch = () => {
    if (user) {
      history.push(PMPRoutes.vendorSearch);
    } else {
      history.push(PublicRoutes.home);
    }
  };


  const handleEditSearch = async (newQuery: string) => {
    if (!address) {
      setSnackbarMessage('Address is required for search');
      return;
    }

    try {
      const result = await searchVendorsMutation.mutateAsync({
        address,
        query: newQuery,
      });

      if (result && result.vendors) {
        // Update the location state with new results
        history.replace(location.pathname, {
          vendors: result.vendors,
          searchQuery: newQuery,
          address,
          disclaimer: result.disclaimer,
          search_query: result.search_query,
          searchId: result.searchId,
        });
      }
    } catch (error) {
      setSnackbarMessage('Failed to search providers. Please try again.');
    }
  };

  const handleCallVendors = async (
    selectedVendors: string[],
    vendorDetails: VendorSearchResult[]
  ) => {
    // Check if user is logged in
    if (!user) {
      // Store current state in sessionStorage for after login, including selected vendors
      sessionStorage.setItem('vendorSearchState', JSON.stringify({
        vendors,
        searchQuery,
        address,
        disclaimer,
        search_query,
        searchId,
        selectedVendors, // Include selected vendors to restore after login
      }));
      
      // Redirect to login page instead of showing modal
      history.push(`${PublicRoutes.login}?redirect=${encodeURIComponent(PMPRoutes.vendorSearchResults)}`);
      return;
    }

    // Track selected vendors in the database
    if (searchId) {
      try {
        await updateSelectedVendorsMutation.mutateAsync({
          searchId,
          selectedVendors,
        });
      } catch {
        // Silently handle the error - tracking is not critical for UX
      }
    }

    // TODO: Implement calling functionality
    // For now, just show the selected vendors
    const vendorNames = vendorDetails.map(v => v.name).join(', ');
    setSnackbarMessage(`Ready to call ${selectedVendors.length} vendor(s): ${vendorNames}`);
  };

  if (!location.state || !vendors) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(180deg, #FFF9F5 0%, #FFFFFF 100%)',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            No results found. Redirecting to search...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <VendorSearchResults
        vendors={vendors}
        searchQuery={searchQuery}
        address={address}
        disclaimer={disclaimer}
        search_query={search_query}
        searchId={searchId}
        isAuthenticated={!!user}
        onNewSearch={handleNewSearch}
        onEditSearch={handleEditSearch}
        onCallVendors={handleCallVendors}
        showBackground
        isCallInProgress={searchVendorsMutation.isPending}
      />


      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </>
  );
};

export default VendorSearchResultsPage;